import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useTranslation } from 'next-i18next';
import { makeRange } from '../utils/page-range';
import useInViewport from '../utils/useInViewport';
import IconLoader from '../public/images/loader.svg';
import IconPages from '../public/images/pages.svg';
import IconRotateAnticlockwise from '../public/images/rotate-anticlockwise.svg';
import IconRotateClockwise from '../public/images/rotate-clockwise.svg';
import IconDelete from '../public/images/delete.svg';

const makeZoomSize = ({
  columns,
  containerInnerWidth = 954,
  gap = 30,
  border = 1,
  padding = 12,
  previewPadding = 5,
}) => {
  const columnWidth = (containerInnerWidth - gap * (columns - 1)) / columns;

  return columnWidth - 2 * (border + padding + previewPadding);
};

const zoomSizes = [
  makeZoomSize({ columns: 5 }),
  makeZoomSize({ columns: 4 }),
  makeZoomSize({ columns: 3 }),
  makeZoomSize({ columns: 2 }),
  makeZoomSize({ columns: 1 }),
];

const PdfCard = ({
  fileIndex,
  pdfDocument,
  pagePromise,
  filename,
  viewType = 'pages',
  group = null,
  zoom = 1,
  deletable,
  rotatable,
  rotation = 0,
  selected = false,
  onClick,
  onRotateAnticlockwise,
  onRotateClockwise,
  onDelete,
}) => {
  const { t } = useTranslation('convert-and-edit');
  const ref = useRef(null);
  const [pdfPage, setPdfPage] = useState(null);
  const [src, setSrc] = useState(null);
  const [status, setStatus] = useState(null);
  const [width, setWidth] = useState(null);
  const [height, setHeight] = useState(null);

  const inViewport = useInViewport(ref, {
    rootMargin: '500px 0px 500px 0px',
  });

  useEffect(() => {
    pagePromise.then((pdfPage) => {
      setPdfPage(pdfPage);
    });
  }, [pagePromise]);

  useLayoutEffect(() => {
    if (!pdfPage) {
      return;
    }

    const viewport = pdfPage.getViewport({ scale: 1 });
    const width = zoomSizes[Math.max(0, Math.min(zoom, zoomSizes.length - 1))];

    const scaledViewport = pdfPage.getViewport({
      scale: width / viewport.width,
      rotation,
    });

    setWidth(scaledViewport.width);
    setHeight(scaledViewport.height);

    if (inViewport) {
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');

      canvas.width = scaledViewport.width;
      canvas.height = scaledViewport.height;

      const renderTask = pdfPage.render({
        canvasContext: context,
        viewport: scaledViewport,
      });

      renderTask.promise
        .then(() => {
          setSrc(canvas.toDataURL());
        })
        .catch((error) => {
          console.error('[PdfCard] pdfjs render error:', error);
          setSrc(null);
        });
    } else {
      setSrc(null);
      setStatus(null);
    }
  }, [pdfPage, inViewport, rotation, zoom]);

  const handleClick = useCallback((event) => {
    if (!pdfPage) {
      return;
    }

    onClick?.(event, {
      fileIndex,
      pdfDocument,
      pages: viewType === 'files'
        ? makeRange(1, pdfDocument.numPages)
        : [pdfPage.pageNumber],
    });
  }, [onClick, fileIndex, viewType, pdfDocument, pdfPage]);

  const handleKeyPress = useCallback((event) => {
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      onClick?.();
    }
  }, [onClick]);

  const handlePreviewLoad = useCallback(() => {
    setStatus('ready');
  }, []);

  const handlePreviewError = useCallback(() => {
    setStatus('error');
  }, []);

  const handleButtonsClick = useCallback((event) => {
    event.stopPropagation();
  }, []);

  const handleButtonsKeyPress = useCallback((event) => {
    if (event.key === ' ' || event.key === 'Enter') {
      event.stopPropagation();
    }
  }, []);

  const handleRotateAnticlockwise = useCallback(() => {
    if (!pdfPage) {
      return;
    }

    onRotateAnticlockwise?.({
      fileIndex,
      pdfDocument,
      pages: viewType === 'files'
        ? makeRange(1, pdfDocument.numPages)
        : [pdfPage.pageNumber],
    });
  }, [onRotateAnticlockwise, fileIndex, viewType, pdfDocument, pdfPage]);

  const handleRotateClockwise = useCallback(() => {
    if (!pdfPage) {
      return;
    }

    onRotateClockwise?.({
      fileIndex,
      pdfDocument,
      pages: viewType === 'files'
        ? makeRange(1, pdfDocument.numPages)
        : [pdfPage.pageNumber],
    });
  }, [onRotateClockwise, fileIndex, viewType, pdfDocument, pdfPage]);

  const handleDelete = useCallback(() => {
    if (!pdfPage) {
      return;
    }

    onDelete?.({
      fileIndex,
      pdfDocument,
      pages: viewType === 'files'
        ? makeRange(1, pdfDocument.numPages)
        : [pdfPage.pageNumber],
      viewType,
    });
  }, [onDelete, fileIndex, viewType, pdfDocument, pdfPage]);

  if (!pdfPage) {
    return null;
  }

  return (
    <div
      className={classNames(
        'pdf-card',
        `pdf-card--zoom-${zoom}`,
        inViewport && !status && 'pdf-card--loading',
        status === 'error' && 'pdf-card--error',
        typeof group === 'number' && `pdf-card--group-${group}`,
        selected && 'pdf-card--selected',
        inViewport && 'pdf-card--in-viewport',
      )}
      tabIndex={0}
      onClick={handleClick}
      onKeyPress={handleKeyPress}
      ref={ref}
    >
      <div className="pdf-card__preview-wrapper">
        <div
          className="pdf-card__preview"
          style={{
            paddingBottom: width && `${height / width * 100}%`,
            width,
          }}
        >
          <img
            src={src}
            alt=""
            draggable="false"
            onLoad={handlePreviewLoad}
            onError={handlePreviewError}
          />
          {inViewport && !status && (
            <IconLoader
              className="pdf-card__preview__icon"
              aria-hidden="true"
            />
          )}
        </div>
      </div>
      <div className="pdf-card__info">
        <div className="pdf-card__page">
          {typeof group === 'number' && (
            <IconPages aria-hidden="true" />
          )}
          {(
            viewType === 'files'
              ? t('editing.page-count', { count: pdfDocument.numPages })
              : pdfPage.pageNumber
          )}
        </div>
        {(onRotateAnticlockwise || onRotateClockwise || onDelete) && (
          <div
            className="pdf-card__buttons"
            onClick={handleButtonsClick}
            onKeyPress={handleButtonsKeyPress}
          >
            {rotatable && (
              <>
                <button
                  className="pdf-card__button"
                  type="button"
                  aria-label={t('editing.rotate-anticlockwise')}
                  onClick={handleRotateAnticlockwise}
                >
                  <IconRotateAnticlockwise aria-hidden="true" />
                </button>
                <button
                  className="pdf-card__button"
                  type="button"
                  aria-label={t('editing.rotate-clockwise')}
                  onClick={handleRotateClockwise}
                >
                  <IconRotateClockwise aria-hidden="true" />
                </button>
              </>
            )}
            {deletable && (
              <button
                className="pdf-card__button"
                type="button"
                aria-label={t('editing.delete')}
                onClick={handleDelete}
              >
                <IconDelete aria-hidden="true" />
              </button>
            )}
          </div>
        )}
        {viewType === 'files' && !!filename && (
          <div className="pdf-card__filename" title={filename}>
            <span>{filename}</span>
          </div>
        )}
      </div>
    </div>
  );
};

PdfCard.minZoom = 0;
PdfCard.maxZoom = zoomSizes.length - 1;

PdfCard.propTypes = {
  fileIndex: PropTypes.number.isRequired,
  pdfDocument: PropTypes.object.isRequired,
  pagePromise: PropTypes.object.isRequired,
  filename: PropTypes.string.isRequired,
  viewType: PropTypes.oneOf([
    'pages',
    'files',
  ]),
  group: PropTypes.number,
  zoom: PropTypes.oneOf([0, 1, 2, 3, 4]),
  deletable: PropTypes.bool,
  rotatable: PropTypes.bool,
  rotation: PropTypes.number,
  selected: PropTypes.bool,
  onClick: PropTypes.func,
  onRotateAnticlockwise: PropTypes.func,
  onRotateClockwise: PropTypes.func,
  onDelete: PropTypes.func,
};

export default PdfCard;
